/**
 * A delivery offer in the dashboard: the form, the request body and the text
 * of a table cell. No React here, so each rule has a test.
 *
 * The add form and the edit form read this one file. They were two copies of
 * the same fields, and a new field then needed the same change two times.
 *
 * An offer answers four questions, and the form has a part for each one:
 * WHAT the customer gets, WHERE (restaurants and areas), WHO (which
 * customers), and WHEN (dates, days, hours).
 *
 * The values are the ones of the API (`api/src/util/deliveryOfferContract.js`).
 * The API checks each request again (`api/src/util/deliveryOfferRequest.js`):
 * the rules here only give the admin the sentence before the request.
 */
import { formatMoney } from "utils/money";
import { formatTime, WEEKDAYS, WEEKDAY_LABELS } from "utils/dateFormat";

/** What the customer gets from an offer. */
export const OFFER_BENEFIT = Object.freeze({
    AMOUNT: "amount",
    FREE: "free",
    PERCENT: "percent",
    FIXED_PRICE: "fixed_price",
});

/** The choices of the field «نوع العرض». */
export const BENEFIT_OPTIONS = Object.freeze([
    { value: OFFER_BENEFIT.AMOUNT, label: "خصم مبلغ من سعر التوصيل" },
    { value: OFFER_BENEFIT.PERCENT, label: "خصم نسبه من سعر التوصيل" },
    { value: OFFER_BENEFIT.FIXED_PRICE, label: "سعر ثابت للتوصيل" },
    { value: OFFER_BENEFIT.FREE, label: "توصيل مجاني" },
]);

/**
 * The number field of each kind of offer. A free offer has no number.
 *
 * @type {Readonly<Record<string, {label: string, placeholder: string, step: number, max?: number}>>}
 */
export const BENEFIT_NUMBER_FIELD = Object.freeze({
    [OFFER_BENEFIT.AMOUNT]: { label: "قيمه الخصم (₪)", placeholder: "مثال: 2", step: 0.5 },
    [OFFER_BENEFIT.PERCENT]: {
        label: "نسبه الخصم (%)",
        placeholder: "مثال: 50",
        step: 1,
        max: 100,
    },
    [OFFER_BENEFIT.FIXED_PRICE]: {
        label: "سعر التوصيل في العرض (₪)",
        placeholder: "مثال: 5",
        step: 0.5,
    },
});

/** Which customers get an offer. */
export const OFFER_CUSTOMER_RULE = Object.freeze({
    EVERYONE: "everyone",
    FIRST_ORDERS: "first_orders",
    NTH_ORDER: "nth_order",
});

/** The choices of the field «الزبائن». */
export const CUSTOMER_RULE_OPTIONS = Object.freeze([
    { value: OFFER_CUSTOMER_RULE.EVERYONE, label: "كل الزبائن" },
    {
        value: OFFER_CUSTOMER_RULE.FIRST_ORDERS,
        label: "الزبائن الجدد: أول طلبات الزبون في المدينه",
    },
    { value: OFFER_CUSTOMER_RULE.NTH_ORDER, label: "مكافأه: طلب واحد بعد عدد من الطلبات" },
]);

/** The label of the count field of each customer rule. */
export const CUSTOMER_COUNT_LABEL = Object.freeze({
    [OFFER_CUSTOMER_RULE.FIRST_ORDERS]: "عدد الطلبات الأولى",
    [OFFER_CUSTOMER_RULE.NTH_ORDER]: "بعد كم طلب",
});

/** Who pays the discount. */
export const OFFER_FUNDER = Object.freeze({ PLATFORM: "platform", VENDOR: "vendor" });

/** The choices of the field «على حساب». */
export const FUNDER_OPTIONS = Object.freeze([
    { value: OFFER_FUNDER.PLATFORM, label: "المنصه" },
    { value: OFFER_FUNDER.VENDOR, label: "المطعم" },
]);

/** The choices of the field «أيام العرض», in the order of the week. */
export const DAY_OPTIONS = Object.freeze(
    WEEKDAYS.map((day) => ({ value: day, label: WEEKDAY_LABELS[day] })),
);

/**
 * The state of the form. Each number is the text of its input, so an empty
 * field stays empty and does not become 0.
 *
 * @typedef {object} OfferForm
 * @property {string} name
 * @property {"amount"|"free"|"percent"|"fixed_price"} benefitType
 * @property {string} amount - the number of the benefit; not used for `free`
 * @property {string} minOrderTotal - the smallest cart, or "" for each cart
 * @property {"platform"|"vendor"} fundedBy
 * @property {boolean} active
 * @property {string|number} cityId
 * @property {boolean} allVendors - each restaurant of the city
 * @property {{value: number, label: string}[]} vendors
 * @property {boolean} allAreas - each area
 * @property {{value: number, label: string}[]} areas
 * @property {"everyone"|"first_orders"|"nth_order"} customerRule
 * @property {string} customerOrderCount - the number of the customer rule
 * @property {string} maxUsesPerCustomer - or "" for no limit
 * @property {{value: string, label: string}[]} days - no day is each day
 * @property {string} startTime - "HH:mm", or ""
 * @property {string} endTime - "HH:mm", or ""
 * @property {string} startDate - YYYY-MM-DD, or ""
 * @property {string} endDate - YYYY-MM-DD, or ""
 */

/**
 * The form of a new offer.
 *
 * @param {{cityId?: string|number}} [defaults]
 * @returns {OfferForm}
 */
export const emptyOfferForm = ({ cityId = "" } = {}) => ({
    name: "",
    benefitType: OFFER_BENEFIT.AMOUNT,
    amount: "",
    minOrderTotal: "",
    fundedBy: OFFER_FUNDER.PLATFORM,
    active: true,
    cityId: cityId ?? "",
    allVendors: false,
    vendors: [],
    allAreas: false,
    areas: [],
    customerRule: OFFER_CUSTOMER_RULE.EVERYONE,
    customerOrderCount: "",
    maxUsesPerCustomer: "",
    days: [],
    startTime: "",
    endTime: "",
    startDate: "",
    endDate: "",
});

/**
 * The three offers that were a setting of a restaurant or of a city until
 * 2026-10-09. Each one opens the add form with its fields set, so the admin
 * adds the restaurant or the number and saves.
 *
 * - `vendor_free`: «قيمه التوصيل المجاني» of the restaurant form.
 * - `new_customers` and `after_orders`: the two switches of the window
 *   «توصيل مجاني» of a city.
 *
 * @type {Readonly<Record<string, {label: string, form: Partial<OfferForm>}>>}
 */
export const OFFER_PRESETS = Object.freeze({
    vendor_free: {
        label: "توصيل مجاني لمطعم",
        form: {
            name: "توصيل مجاني",
            benefitType: OFFER_BENEFIT.FREE,
            allAreas: true,
            fundedBy: OFFER_FUNDER.VENDOR,
        },
    },
    new_customers: {
        label: "توصيل مجاني للزبائن الجدد",
        form: {
            name: "توصيل مجاني للزبائن الجدد",
            benefitType: OFFER_BENEFIT.FREE,
            allVendors: true,
            allAreas: true,
            customerRule: OFFER_CUSTOMER_RULE.FIRST_ORDERS,
        },
    },
    after_orders: {
        label: "توصيل مجاني بعد عدد من الطلبات",
        form: {
            name: "توصيل مجاني بعد عدد من الطلبات",
            benefitType: OFFER_BENEFIT.FREE,
            allVendors: true,
            allAreas: true,
            customerRule: OFFER_CUSTOMER_RULE.NTH_ORDER,
        },
    },
});

/**
 * The form of a new offer from a preset. A key that is not a preset gives the
 * empty form.
 *
 * @param {string|null|undefined} key - a key of `OFFER_PRESETS`
 * @param {{cityId?: string|number, vendor?: {value: number, label: string}|null}} [start]
 *   the city of the form, and the restaurant that the admin came from
 * @returns {OfferForm}
 */
export const presetOfferForm = (key, { cityId = "", vendor = null } = {}) => {
    const preset = Object.hasOwn(OFFER_PRESETS, key ?? "") ? OFFER_PRESETS[key] : null;
    const form = { ...emptyOfferForm({ cityId }), ...(preset?.form ?? {}) };

    // A list of one restaurant only for an offer with a list.
    if (vendor && !form.allVendors) {
        form.vendors = [vendor];
        if (preset) form.name = `${preset.form.name}: ${vendor.label}`;
    }
    return form;
};

/** A number of the API as the text of an input: "100", not "100.00". */
const numberText = (value) => {
    const number = Number(value);
    return value === null || value === undefined || value === "" || !Number.isFinite(number)
        ? ""
        : String(number);
};

/** A value of the API when it is one of the known ones, or the default. */
const oneOf = (values, value, fallback) =>
    Object.values(values).includes(value) ? value : fallback;

/** A TIME of the API ("12:00:00") as the value of the time field ("12:00"). */
const timeText = (value) =>
    typeof value === "string" && /^\d{2}:\d{2}/.test(value) ? value.slice(0, 5) : "";

/**
 * The form of an offer that exists.
 *
 * @param {object} offer - a row of `GET /api/delivery-offers`
 * @returns {OfferForm}
 */
export const offerToForm = (offer) => {
    const benefitType = oneOf(OFFER_BENEFIT, offer?.benefit_type, OFFER_BENEFIT.AMOUNT);
    const customerRule = oneOf(
        OFFER_CUSTOMER_RULE,
        offer?.customer_rule,
        OFFER_CUSTOMER_RULE.EVERYONE,
    );
    const minimum = Number(offer?.min_order_total);
    const days = Array.isArray(offer?.days_of_week) ? offer.days_of_week : [];

    return {
        name: offer?.name ?? "",
        benefitType,
        // A free offer has the number 0. The field is empty then, so a change
        // to an other kind asks for a number.
        amount: benefitType === OFFER_BENEFIT.FREE ? "" : numberText(offer?.discount_amount),
        minOrderTotal: minimum > 0 ? numberText(minimum) : "",
        fundedBy: oneOf(OFFER_FUNDER, offer?.funded_by, OFFER_FUNDER.PLATFORM),
        active: !!offer?.active,
        cityId: offer?.cityId ?? "",
        allVendors: !!offer?.all_vendors,
        vendors: (offer?.vendorUsers ?? []).map((vendor) => ({
            value: vendor.id,
            label: vendor.name,
        })),
        allAreas: !!offer?.all_areas,
        areas: (offer?.areas ?? []).map((area) => ({ value: area.id, label: area.name })),
        customerRule,
        customerOrderCount:
            customerRule === OFFER_CUSTOMER_RULE.EVERYONE
                ? ""
                : numberText(offer?.customer_order_count),
        maxUsesPerCustomer: numberText(offer?.max_uses_per_customer),
        days: DAY_OPTIONS.filter((option) => days.includes(option.value)),
        startTime: timeText(offer?.start_time),
        endTime: timeText(offer?.end_time),
        startDate: offer?.start_date ?? "",
        endDate: offer?.end_date ?? "",
    };
};

/** A whole number of 1 or more, as the text of an input. */
const isCount = (text) => /^\d+$/.test(String(text).trim()) && Number(text) >= 1;

/**
 * What is wrong with the form, as a sentence for the admin.
 *
 * @param {OfferForm} form
 * @param {{needsCity: boolean}} options - the add form picks a city; an offer
 *   that exists keeps the one it has
 * @returns {string|null} null when the form can be sent
 */
export const validateOfferForm = (form, { needsCity }) => {
    if (needsCity && !form.cityId) return "اختر المدينة أولا";

    const number = Number(form.amount);
    if (form.benefitType === OFFER_BENEFIT.AMOUNT && !(number > 0)) {
        return "اكتب قيمه الخصم: رقم أكبر من 0";
    }
    if (form.benefitType === OFFER_BENEFIT.PERCENT && !(number > 0 && number <= 100)) {
        return "اكتب نسبه الخصم: رقم أكبر من 0 وحتى 100";
    }
    if (form.benefitType === OFFER_BENEFIT.FIXED_PRICE && !(number > 0)) {
        return "اكتب سعر التوصيل في العرض: رقم أكبر من 0";
    }
    if (form.minOrderTotal !== "" && !(Number(form.minOrderTotal) > 0)) {
        return "الحد الأدنى للطلب رقم أكبر من 0. اتركه فارغا ليشمل العرض كل الطلبات";
    }

    if (!form.allVendors && form.vendors.length === 0) {
        return "اختر مطعم واحد على الأقل، أو اختر «كل مطاعم المدينه»";
    }
    if (!form.allAreas && form.areas.length === 0) {
        return "اختر منطقه واحده على الأقل، أو اختر «كل المناطق»";
    }

    if (form.customerRule !== OFFER_CUSTOMER_RULE.EVERYONE && !isCount(form.customerOrderCount)) {
        return "اكتب عدد الطلبات: رقم صحيح أكبر من 0";
    }
    if (
        form.customerRule !== OFFER_CUSTOMER_RULE.NTH_ORDER &&
        form.maxUsesPerCustomer !== "" &&
        !isCount(form.maxUsesPerCustomer)
    ) {
        return "عدد مرات الاستخدام رقم صحيح أكبر من 0. اتركه فارغا لعدد غير محدود";
    }

    if (!!form.startTime !== !!form.endTime) {
        return "اكتب وقت البدايه ووقت النهايه معا، أو اتركهما فارغين";
    }
    if (form.startTime && form.startTime === form.endTime) {
        return "وقت البدايه ووقت النهايه متساويان";
    }
    if (form.startDate && form.endDate && form.startDate > form.endDate) {
        return "تاريخ البدايه يجب أن يكون قبل تاريخ النهايه";
    }
    return null;
};

/**
 * The body of the create request, or of the edit request.
 *
 * It has EACH field, also one that the admin did not touch. So an edit puts
 * the offer in the state of the form: a field that the admin emptied is
 * removed, and does not keep its stored value.
 *
 * An offer keeps its city, so the edit request has no `cityId`.
 *
 * @param {OfferForm} form
 * @param {{withCity: boolean}} options
 * @returns {object}
 */
export const formToBody = (form, { withCity }) => {
    const forEachCustomer = form.customerRule === OFFER_CUSTOMER_RULE.EVERYONE;
    const oneOrder = form.customerRule === OFFER_CUSTOMER_RULE.NTH_ORDER;

    return {
        name: form.name,
        benefit_type: form.benefitType,
        // A free offer has no number. The API saves 0 for it.
        ...(form.benefitType === OFFER_BENEFIT.FREE
            ? {}
            : { discount_amount: Number(form.amount) }),
        min_order_total: form.minOrderTotal === "" ? null : Number(form.minOrderTotal),
        funded_by: form.fundedBy,
        active: form.active,
        ...(withCity ? { cityId: Number(form.cityId) } : {}),
        // "Each one" has no list: the offer follows the city, not a list of today.
        all_vendors: form.allVendors,
        vendorIds: form.allVendors ? [] : form.vendors.map((vendor) => vendor.value),
        all_areas: form.allAreas,
        areaIds: form.allAreas ? [] : form.areas.map((area) => area.value),
        customer_rule: form.customerRule,
        customer_order_count: forEachCustomer ? null : Number(form.customerOrderCount),
        // One order number is one use. A limit beside it says nothing.
        max_uses_per_customer:
            oneOrder || form.maxUsesPerCustomer === "" ? null : Number(form.maxUsesPerCustomer),
        days_of_week: form.days.length === 0 ? null : form.days.map((day) => day.value),
        start_time: form.startTime || null,
        end_time: form.endTime || null,
        start_date: form.startDate || null,
        end_date: form.endDate || null,
    };
};

/** A number with no ".00": 50, 7.5. */
const plain = (value) => Number(value).toLocaleString("en", { maximumFractionDigits: 2 });

/**
 * The cell «العرض»: what the customer gets.
 *
 * @param {object} offer
 * @returns {string}
 */
export const describeBenefit = (offer) => {
    switch (offer?.benefit_type) {
        case OFFER_BENEFIT.FREE:
            return "توصيل مجاني";
        case OFFER_BENEFIT.PERCENT:
            return `خصم ${plain(offer.discount_amount)}%`;
        case OFFER_BENEFIT.FIXED_PRICE:
            return `توصيل بسعر ${formatMoney(offer.discount_amount)}`;
        default:
            return `خصم ${formatMoney(offer?.discount_amount)}`;
    }
};

/** «طلب واحد», «طلبين», «3 طلبات», «11 طلبا». */
const ordersWord = (count) => {
    if (count === 1) return "طلب واحد";
    if (count === 2) return "طلبين";
    return count <= 10 ? `${count} طلبات` : `${count} طلبا`;
};

/** «مره واحده», «مرتين», «3 مرات», «11 مره». */
const timesWord = (count) => {
    if (count === 1) return "مره واحده";
    if (count === 2) return "مرتين";
    return count <= 10 ? `${count} مرات` : `${count} مره`;
};

/**
 * The cell «الشرط»: which orders get the offer. Each condition of the offer
 * is one part. An offer with no condition says «كل الطلبات».
 *
 * @param {object} offer
 * @returns {string}
 */
export const describeCondition = (offer) => {
    const parts = [];
    const count = Number(offer?.customer_order_count);

    if (Number(offer?.min_order_total) > 0)
        parts.push(`للطلبات من ${formatMoney(offer.min_order_total)}`);
    if (offer?.customer_rule === OFFER_CUSTOMER_RULE.FIRST_ORDERS) {
        parts.push(count === 1 ? "أول طلب للزبون" : `أول ${ordersWord(count)} للزبون`);
    }
    if (offer?.customer_rule === OFFER_CUSTOMER_RULE.NTH_ORDER) {
        parts.push(`مره واحده بعد ${ordersWord(count)}`);
    }
    if (Number(offer?.max_uses_per_customer) > 0) {
        parts.push(`${timesWord(Number(offer.max_uses_per_customer))} لكل زبون`);
    }

    return parts.length > 0 ? parts.join(" · ") : "كل الطلبات";
};

/**
 * The days and the hours of an offer, for the cell «الفتره». "" when the
 * offer is for each day and the whole day.
 *
 * @param {object} offer
 * @returns {string}
 */
export const describeSchedule = (offer) => {
    const parts = [];
    const days = Array.isArray(offer?.days_of_week) ? offer.days_of_week : [];

    if (days.length > 0) {
        parts.push(
            WEEKDAYS.filter((day) => days.includes(day))
                .map((day) => WEEKDAY_LABELS[day])
                .join("، "),
        );
    }
    if (offer?.start_time && offer?.end_time) {
        parts.push(`${formatTime(offer.start_time)} – ${formatTime(offer.end_time)}`);
    }
    return parts.join(" · ");
};

/**
 * The cell «المطاعم»: the restaurants of an offer.
 *
 * @param {object} offer
 * @returns {string}
 */
export const describeVendors = (offer) =>
    offer?.all_vendors ? "كل المطاعم" : `${offer?.vendorUsers?.length ?? 0} مطعم`;

/**
 * The cell «المناطق»: the areas of an offer.
 *
 * @param {object} offer
 * @returns {string}
 */
export const describeAreas = (offer) =>
    offer?.all_areas ? "كل المناطق" : `${offer?.areas?.length ?? 0} منطقه`;

/**
 * The cell «على حساب»: who pays the discount.
 *
 * @param {object} offer
 * @returns {string}
 */
export const describeFunder = (offer) =>
    offer?.funded_by === OFFER_FUNDER.VENDOR ? "المطعم" : "المنصه";

/**
 * Does the offer depend on the cart or on the customer? The card of a
 * restaurant in the customer app shows the price of an offer only when this
 * is false: a card has no cart, and it is the same for each customer.
 *
 * @param {OfferForm} form
 * @returns {boolean}
 */
export const formHasCondition = (form) =>
    Number(form.minOrderTotal) > 0 ||
    form.customerRule !== OFFER_CUSTOMER_RULE.EVERYONE ||
    Number(form.maxUsesPerCustomer) > 0;

/**
 * Do the hours of the form pass midnight? The form then says that the offer
 * ends on the next day.
 *
 * @param {OfferForm} form
 * @returns {boolean}
 */
export const hoursPassMidnight = (form) =>
    !!form.startTime && !!form.endTime && form.endTime < form.startTime;

/**
 * The state of an offer on a day. The dates of an offer are inclusive, and
 * the day is the Palestine date: the API reads them the same way
 * (`buildActiveDeliveryOfferWhere`), and the home page counts `active` with
 * the same rule.
 *
 * The days and the hours of an offer are not in the state: an offer for
 * Friday is `active` on Monday too, and «الفتره» says its days.
 */
export const OFFER_STATE = Object.freeze({
    ACTIVE: "active",
    SCHEDULED: "scheduled",
    ENDED: "ended",
    OFF: "off",
});

/**
 * The pill of each state: its words, and its tone of `.app-presence`
 * (components/Ui/AppPresenceBadge).
 *
 * @type {Readonly<Record<string, {label: string, tone: string}>>}
 */
export const OFFER_STATE_PILL = Object.freeze({
    [OFFER_STATE.ACTIVE]: { label: "نشط", tone: "on" },
    [OFFER_STATE.SCHEDULED]: { label: "مجدول", tone: "idle" },
    [OFFER_STATE.ENDED]: { label: "منتهي", tone: "off" },
    [OFFER_STATE.OFF]: { label: "متوقف", tone: "none" },
});

/**
 * @param {object} offer - a row of `GET /api/delivery-offers`
 * @param {string} today - the Palestine date, "YYYY-MM-DD"
 * @returns {"active"|"scheduled"|"ended"|"off"}
 */
export const offerState = (offer, today) => {
    if (!offer?.active) return OFFER_STATE.OFF;
    if (offer.end_date && offer.end_date < today) return OFFER_STATE.ENDED;
    if (offer.start_date && offer.start_date > today) return OFFER_STATE.SCHEDULED;
    return OFFER_STATE.ACTIVE;
};

/**
 * The filters of the offers page. An empty value is "each one".
 *
 * @typedef {object} OfferFilters
 * @property {string} [search] - a part of the name of the offer, or of a
 *   restaurant of its list, or the number of the offer
 * @property {string} [cityId]
 * @property {string} [state] - a value of `OFFER_STATE`
 * @property {string} [benefit] - a value of `OFFER_BENEFIT`
 * @property {string} [customerRule] - a value of `OFFER_CUSTOMER_RULE`
 */

/**
 * The offers that the filters of the page leave.
 *
 * @param {object[]} offers
 * @param {OfferFilters} filters
 * @param {string} today - the Palestine date, "YYYY-MM-DD"
 * @returns {object[]}
 */
export const filterOffers = (offers, filters, today) => {
    const search = (filters.search ?? "").trim().toLowerCase();

    return offers.filter((offer) => {
        if (filters.cityId && String(offer.cityId) !== String(filters.cityId)) return false;
        if (filters.state && offerState(offer, today) !== filters.state) return false;
        if (filters.benefit && (offer.benefit_type ?? OFFER_BENEFIT.AMOUNT) !== filters.benefit)
            return false;
        if (
            filters.customerRule &&
            (offer.customer_rule ?? OFFER_CUSTOMER_RULE.EVERYONE) !== filters.customerRule
        ) {
            return false;
        }
        if (!search) return true;

        const texts = [
            offer.name,
            String(offer.id ?? ""),
            ...(offer.vendorUsers ?? []).map((vendor) => vendor.name),
        ];
        return texts.some((text) =>
            String(text ?? "")
                .toLowerCase()
                .includes(search),
        );
    });
};
